<template>
  <section class="flex align-middle justify-center sm:py-40">
    <div class="flex flex-col w-full sm:w-auto px-8 sm:px-0">
      <Card class="!bg-transparent sm:!bg-white box-shadow-none sm:box-shadow-sm sm:w-448 max-w-screen mt-20 sm:mt-0 p-24">
        <form id="request-upgrade-form" @submit.prevent="submit()">
          <h1 class="text-h1 mb-24">Free account upgrade</h1>
          <fieldset>
            <legend class="pb-16 text-body text-grey-body">
              Check if your company’s benefits include unlimited access to all of Sittercity’s features
            </legend>
            <SearchComboBox
              id="companySearchBox"
              ref="companySearchBox"
              v-model="employerNameInput"
              allowEmptyResults
              :results="filteredCorporateClients"
              type="text"
              labelName="Your employer’s name"
              :value="selectedCompanySlug"
              :initialValue="initialValue"
              class="mb-16"
              :errorText="employerErrorText"
              :status="employerStatus"
              @selected="updateValue"
              @valueUpdated="filterResults"
            />
            <text-field
              id="workEmail"
              v-model="workEmail"
              labelName="Work email"
              :helperText="emailFieldHelperText"
              name="workEmail"
              type="email"
              autocomplete="email"
              class="mb-16"
              :status="workEmailStatus"
            />
          </fieldset>
          <Button class="w-full" variant="primary" value="Check eligibility" size="medium" type="submit" />
        </form>
      </Card>
      <popup id="errorPopup" v-model="showPopup" popupHeader="We couldn’t send your request" @close="closePopup">
        <p class="text-body mb-16">
          There was an error submitting your request. Try again and if the error continues,
          <a
            href="https://support.sittercity.com/hc/en-us/requests/new?ticket_form_id=5652055814427"
            class="text-grey-clickable underline"
            >contact Customer Success</a
          >
        </p>
        <Button
          id="tryAgain"
          variant="primary"
          value="Try again"
          size="medium"
          class="w-full"
          :loading="isLoading"
          @click="closePopup"
        />
      </popup>
    </div>
  </section>
</template>

<script lang="ts">
import SearchComboBox from '@/components/search_combo_box/SearchComboBox.vue';
import Button from '@/components/button/Button.vue';
import Card from '@/components/card/Card.vue';
import Popup from '@/components/popup/Popup.vue';
import TextField from '@/components/textfield/TextField.vue';
import { sendSegmentEvent } from '@/lib/segment';
import helperStatus from '@/helpers/status';
import { CORPORATE_CLIENTS_QUERY } from '@/graphql/bh_benefits/queries';
import { validateEmail } from '@/helpers/emailHelper';
import { ADD_EMPLOYER } from '@/graphql/bh_benefits/mutations';

export default {
  name: 'RequestUpgrade',
  components: {
    SearchComboBox,
    Button,
    Card,
    Popup,
    TextField,
  },
  data(): any {
    return {
      showPopup: false,
      corporateClients: [],
      employerNameInput: '',
      selectedCompanySlug: this.$route.query.slug,
      employerStatus: helperStatus.Neutral,
      filteredCorporateClients: [],
      workEmail: '',
      workEmailStatus: helperStatus.Neutral,
      employerErrorText: "Employer's name is required",
      initialValue: '',
      isLoading: false,
    };
  },
  computed: {
    emailFieldHelperText() {
      if (this.workEmailStatus === helperStatus.Error) {
        if (this.workEmail.length) {
          return 'Enter a valid email';
        }
        return 'Work email is required';
      }
      return 'Enter the email you use at work';
    },
  },
  watch: {
    corporateClients(newValue) {
      const companySelectedInfo = newValue.filter((client) => {
        return client.value === this.selectedCompanySlug;
      });
      if (companySelectedInfo[0]) {
        this.selectedCompanySlug = companySelectedInfo[0].value;
        this.employerNameInput = companySelectedInfo[0].display;
        this.initialValue = companySelectedInfo[0].display;
      }
    },
    employerNameInput(value) {
      if (this.employerStatus === helperStatus.Error) {
        if (this.validateEmployerEntered(value)) {
          this.employerStatus = helperStatus.Neutral;
        }
      }
    },
  },
  mounted() {
    this.selectedCompanySlug = this.$route.query.slug;
  },
  methods: {
    closePopup() {
      this.showPopup = false;
    },
    invalidEmployerEntered() {
      sendSegmentEvent('Invalid Employer Entered', {
        employerEntered: this.employerNameInput,
      });
    },
    validateEmployerEntered(employer: string): boolean {
      return employer?.trim().length > 0;
    },
    submit() {
      this.employerStatus = helperStatus.Neutral;
      const [emailValid, employerValid] = [
        validateEmail(this.workEmail),
        this.validateEmployerEntered(this.selectedCompanySlug ? this.selectedCompanySlug : this.employerNameInput),
      ];
      if (!emailValid) {
        this.workEmailStatus = helperStatus.Error;
      }
      if (!employerValid) {
        this.employerStatus = helperStatus.Error;
      }

      if (emailValid && employerValid) {
        this.addEmployerMutation();
        sendSegmentEvent('CorporateBenefit_RequestUpgrade', {
          employerSlugEntered: this.selectedCompanySlug,
          employerNameInput: this.employerNameInput,
        });
      }
    },
    filterResults(input) {
      this.employerNameInput = input;
      const searchRegex = new RegExp(input, 'i');
      this.filteredCorporateClients = this.corporateClients.filter((item) => searchRegex.test(item.display));
    },
    updateValue(v): void {
      this.selectedCompanySlug = v.value;
      this.employerNameInput = v.display;
    },
    async addEmployerMutation() {
      this.isLoading = true;
      await this.$nextTick();
      try {
        await this.$apollo.mutate({
          mutation: ADD_EMPLOYER,
          variables: {
            workEmail: this.workEmail,
            employerSlug: this.selectedCompanySlug,
            employerName: this.employerNameInput,
            source: 'hdyhau',
          },
        });
        this.$router.push({
          name: 'registration-upgrade-request-sent',
          query: { redirectPath: '/account/seekers/photo#/upload' },
        });
      } catch (_error) {
        this.showPopup = true;
      } finally {
        this.isLoading = false;
      }
    },
  },
  apollo: {
    corporateClients: {
      query: CORPORATE_CLIENTS_QUERY,
      result({ data }) {
        const corporateClientsForComboBox = data.corporateClients.map((corporateClient) => {
          return { display: corporateClient.name, value: corporateClient.slug };
        });
        this.corporateClients = corporateClientsForComboBox;
        this.filteredCorporateClients = corporateClientsForComboBox;
      },
    },
  },
};
</script>
